import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import {
  ClipboardList,
  Download,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  Users,
  FileCheck,
  UserCheck,
  PackageX,
  X,
  Calendar,
  Mail,
  MapPin,
  MessageSquare,
  ArrowUpDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EosSurveyService } from '../../services/EosSurveyService';
import { EosSurveySubmission } from '../../@types/eosSurvey';
import { CSVExportService } from '../../services/CSVExportService';
import {
  Card,
  Button,
  Heading2,
  Text,
  PageContainer,
  MainContent,
} from '../../styles/StyledComponents';
import {
  ThemeColors,
  Spacing,
  Typography,
  BorderRadius,
  Shadows,
} from '../../styles/ThemeStyles';

/* ─────────────────── Styled Components ─────────────────── */

const InnerContainer = styled.div`
  width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.xl};
  flex-wrap: wrap;
  gap: ${Spacing.md};
`;

const PageTitle = styled.h1`
  font-family: ${Typography.fontFamily.secondary};
  font-size: 1.6rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin: 0;

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.lg};
  font-weight: ${Typography.fontWeight.medium};
`;

/* ── Stats Cards ── */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)<{ accentColor?: string }>`
  padding: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  border-left: 4px solid ${(props) => props.accentColor || ThemeColors.primary.main};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
  }
`;

const StatIcon = styled.div<{ bgColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${BorderRadius.lg};
  background: ${(props) => props.bgColor || ThemeColors.primary.main + '15'};
  flex-shrink: 0;

  svg {
    color: ${(props) => props.bgColor?.replace('15', '') || ThemeColors.primary.main};
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 1.6rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  line-height: 1.2;
`;

const StatLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-top: 2px;
`;

/* ── Search / Filter ── */

const ToolbarCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  padding: ${Spacing.md} ${Spacing.lg};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex: 1;
  min-width: 200px;
  background: ${ThemeColors.gray[50]};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.xs} ${Spacing.md};

  input {
    border: none;
    background: transparent;
    outline: none;
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.primary};
    width: 100%;
    padding: ${Spacing.xs} 0;
    font-family: ${Typography.fontFamily.primary};
  }

  svg {
    color: ${ThemeColors.text.secondary};
    flex-shrink: 0;
  }
`;

const ResultCount = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  white-space: nowrap;
`;

/* ── Table ── */

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ sortable?: boolean }>`
  text-align: left;
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  background: ${ThemeColors.gray[50]};
  border-bottom: 2px solid ${ThemeColors.border.light};
  white-space: nowrap;
  user-select: none;
  cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};

  &:hover {
    ${(props) =>
      props.sortable &&
      `color: ${ThemeColors.primary.main}; background: ${ThemeColors.gray[100]};`}
  }
`;

const ThContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const Td = styled.td`
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  border-bottom: 1px solid ${ThemeColors.border.light};
  vertical-align: top;
`;

const Tr = styled.tr<{ clickable?: boolean }>`
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  &:hover {
    background: ${ThemeColors.gray[50]};
  }
`;

const YesBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  background: ${ThemeColors.success.main}20;
  color: ${ThemeColors.success.dark};
`;

const NoBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  background: ${ThemeColors.error.main}18;
  color: ${ThemeColors.error.dark};
`;

/* ── Detail Panel ── */

const DetailRow = styled.tr``;

const DetailCell = styled.td`
  padding: 0;
  border-bottom: 2px solid ${ThemeColors.primary.main}30;
`;

const DetailPanel = styled.div`
  background: ${ThemeColors.gray[50]};
  padding: ${Spacing.lg} ${Spacing.xl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md} ${Spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: ${Spacing.md};
  }
`;

const DetailItem = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${(props) => (props.fullWidth ? '1 / -1' : 'auto')};
`;

const DetailLabel = styled.div`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs};
`;

const DetailValue = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  line-height: 1.5;
  background: white;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

/* ── Empty State ── */

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md};

  svg {
    opacity: 0.4;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl} 0;
  gap: ${Spacing.md};
  color: ${ThemeColors.text.secondary};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/* ─────────────────── Component ─────────────────── */

type SortField = 'submittedByName' | 'lastDayWorked' | 'submittedAt';
type SortDir = 'asc' | 'desc';

export const EosSurveyManagementScreen: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<EosSurveySubmission[]>([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('submittedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  /* ── Fetch ── */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await EosSurveyService.getAllSubmissions();
        setSubmissions(data);
      } catch (err: any) {
        console.error('Error loading submissions:', err);
        toast.error('Failed to load survey submissions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Filtered + sorted ── */
  const filtered = useMemo(() => {
    let list = [...submissions];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.submittedByName?.toLowerCase().includes(q) ||
          s.submittedByEmail?.toLowerCase().includes(q) ||
          s.feedback?.toLowerCase().includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'submittedByName':
          aVal = (a.submittedByName || '').toLowerCase();
          bVal = (b.submittedByName || '').toLowerCase();
          break;
        case 'lastDayWorked':
          aVal = a.lastDayWorked || '';
          bVal = b.lastDayWorked || '';
          break;
        case 'submittedAt':
        default: {
          const toMs = (v: any) => {
            if (!v) return 0;
            if (v.toDate) return v.toDate().getTime();
            return new Date(v).getTime();
          };
          aVal = toMs(a.submittedAt);
          bVal = toMs(b.submittedAt);
          break;
        }
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [submissions, search, sortField, sortDir]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const total = submissions.length;
    const roe = submissions.filter((s) => s.requireROE).length;
    const returning = submissions.filter((s) => s.interestedReturning2027).length;
    const notMailed = submissions.filter((s) => !s.mailedBackEquipment).length;
    return { total, roe, returning, notMailed };
  }, [submissions]);

  /* ── Sort toggle ── */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── CSV Export ── */
  const handleExport = async () => {
    if (submissions.length === 0) {
      toast.error('No submissions to export');
      return;
    }

    try {
      const data = submissions.map((s) => {
        const toDateStr = (v: any) => {
          if (!v) return '';
          if (v.toDate) return v.toDate().toLocaleDateString('en-US');
          return new Date(v).toLocaleDateString('en-US');
        };

        return {
          'Respondent First Name': s.respondentFirstName || '',
          'Respondent Last Name': s.respondentLastName || '',
          'Account Name': s.submittedByName || '',
          'Email': s.submittedByEmail || '',
          'Last Day Worked': s.lastDayWorked || '',
          'Require ROE': s.requireROE ? 'Yes' : 'No',
          'Contact Info Updates': s.hasContactUpdates ? 'Yes' : 'No',
          'Contact Updates Detail': s.contactUpdates || '',
          'Mailed Back Equipment': s.mailedBackEquipment ? 'Yes' : 'No',
          'Reason Not Mailed Back': s.reasonNotMailedBack || '',
          'Returning 2027': s.interestedReturning2027 ? 'Yes' : 'No',
          'Territory Adjustments': s.wantsTerritoryAdjustments ? 'Yes' : 'No',
          'Requested Territory Changes': s.requestedTerritoryChanges || '',
          'Feedback': s.feedback || '',
          'Submitted At': toDateStr(s.submittedAt),
        };
      });

      await CSVExportService.exportDataToCSV(data, {
        filename: `eos_survey_2026_export_${new Date().toISOString().split('T')[0]}.csv`,
      });

      toast.success('CSV exported successfully');
    } catch (err: any) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  /* ── Helpers ── */
  const formatDate = (val: any): string => {
    if (!val) return '—';
    if (typeof val === 'string') {
      const d = new Date(val + 'T00:00:00');
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    const d = val.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown size={14} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  /* ── Render ── */
  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
      <Toaster position="top-center" />

      <PageHeader>
        <PageTitle>
          <ClipboardList size={24} />
          EOS Survey 2026 — Submissions
        </PageTitle>
        <ExportButton onClick={handleExport} disabled={submissions.length === 0}>
          <Download size={18} />
          Export CSV
        </ExportButton>
      </PageHeader>

      {/* Stats */}
      <StatsGrid>
        <StatCard accentColor={ThemeColors.primary.main}>
          <StatIcon bgColor={ThemeColors.primary.main + '15'}>
            <Users size={22} color={ThemeColors.primary.main} />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Submissions</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard accentColor={ThemeColors.info.main}>
          <StatIcon bgColor={ThemeColors.info.main + '15'}>
            <FileCheck size={22} color={ThemeColors.info.main} />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.roe}</StatValue>
            <StatLabel>ROE Requests</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard accentColor={ThemeColors.success.main}>
          <StatIcon bgColor={ThemeColors.success.main + '15'}>
            <UserCheck size={22} color={ThemeColors.success.main} />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.returning}</StatValue>
            <StatLabel>Returning 2027</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard accentColor={ThemeColors.warning.main}>
          <StatIcon bgColor={ThemeColors.warning.main + '15'}>
            <PackageX size={22} color={ThemeColors.warning.dark} />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.notMailed}</StatValue>
            <StatLabel>Equipment Not Returned</StatLabel>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Search */}
      <ToolbarCard>
        <SearchInput>
          <Search size={16} />
          <input
            placeholder="Search by name, email, or feedback…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            id="eos-survey-search"
          />
          {search && (
            <X
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={() => setSearch('')}
            />
          )}
        </SearchInput>
        <ResultCount>
          {filtered.length} of {submissions.length} submissions
        </ResultCount>
      </ToolbarCard>

      {/* Table */}
      {loading ? (
        <LoadingContainer>
          <Loader2 size={32} />
          <span>Loading submissions…</span>
        </LoadingContainer>
      ) : submissions.length === 0 ? (
        <EmptyState>
          <ClipboardList size={48} />
          <Heading2 style={{ color: ThemeColors.text.secondary }}>
            No Submissions Yet
          </Heading2>
          <Text>
            Survey submissions from sales reps will appear here.
          </Text>
        </EmptyState>
      ) : (
        <TableCard>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th
                    sortable
                    onClick={() => handleSort('submittedByName')}
                  >
                    <ThContent>
                      Respondent <SortIcon field="submittedByName" />
                    </ThContent>
                  </Th>
                  <Th>Account</Th>
                  <Th>Email</Th>
                  <Th
                    sortable
                    onClick={() => handleSort('lastDayWorked')}
                  >
                    <ThContent>
                      Last Day Worked <SortIcon field="lastDayWorked" />
                    </ThContent>
                  </Th>
                  <Th>ROE</Th>
                  <Th>Returning</Th>
                  <Th>Equipment</Th>
                  <Th
                    sortable
                    onClick={() => handleSort('submittedAt')}
                  >
                    <ThContent>
                      Submitted <SortIcon field="submittedAt" />
                    </ThContent>
                  </Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <React.Fragment key={s.id}>
                    <Tr
                      clickable
                      onClick={() =>
                        setExpandedId(expandedId === s.id ? null : s.id)
                      }
                    >
                      <Td style={{ fontWeight: 600 }}>
                        {s.respondentFirstName && s.respondentLastName
                          ? `${s.respondentFirstName} ${s.respondentLastName}`
                          : s.submittedByName || '—'}
                      </Td>
                      <Td>{s.submittedByName || '—'}</Td>
                      <Td>{s.submittedByEmail || '—'}</Td>
                      <Td>{formatDate(s.lastDayWorked)}</Td>
                      <Td>
                        {s.requireROE ? (
                          <YesBadge>Yes</YesBadge>
                        ) : (
                          <NoBadge>No</NoBadge>
                        )}
                      </Td>
                      <Td>
                        {s.interestedReturning2027 ? (
                          <YesBadge>Yes</YesBadge>
                        ) : (
                          <NoBadge>No</NoBadge>
                        )}
                      </Td>
                      <Td>
                        {s.mailedBackEquipment ? (
                          <YesBadge>Yes</YesBadge>
                        ) : (
                          <NoBadge>No</NoBadge>
                        )}
                      </Td>
                      <Td>{formatDate(s.submittedAt)}</Td>
                      <Td>
                        {expandedId === s.id ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </Td>
                    </Tr>

                    {expandedId === s.id && (
                      <DetailRow>
                        <DetailCell colSpan={9}>
                          <DetailPanel>
                            <DetailItem>
                              <DetailLabel>
                                <Calendar
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Last Day Worked
                              </DetailLabel>
                              <DetailValue>
                                {formatDate(s.lastDayWorked)}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                <FileCheck
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Require ROE
                              </DetailLabel>
                              <DetailValue>
                                {s.requireROE ? 'Yes' : 'No'}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                <Mail
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Contact Info Updates
                              </DetailLabel>
                              <DetailValue>
                                {s.hasContactUpdates ? 'Yes' : 'No'}
                                {s.contactUpdates && (
                                  <>
                                    <br />
                                    {s.contactUpdates}
                                  </>
                                )}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                <PackageX
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Equipment/Paperwork Mailed Back
                              </DetailLabel>
                              <DetailValue>
                                {s.mailedBackEquipment ? 'Yes' : 'No'}
                                {s.reasonNotMailedBack && (
                                  <>
                                    <br />
                                    <em>Reason:</em> {s.reasonNotMailedBack}
                                  </>
                                )}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                <UserCheck
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Returning for 2027
                              </DetailLabel>
                              <DetailValue>
                                {s.interestedReturning2027 ? 'Yes' : 'No'}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                <MapPin
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Territory Adjustments
                              </DetailLabel>
                              <DetailValue>
                                {s.wantsTerritoryAdjustments ? 'Yes' : 'No'}
                                {s.requestedTerritoryChanges && (
                                  <>
                                    <br />
                                    {s.requestedTerritoryChanges}
                                  </>
                                )}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem fullWidth>
                              <DetailLabel>
                                <MessageSquare
                                  size={12}
                                  style={{ marginRight: 4 }}
                                />
                                Feedback / Suggestions
                              </DetailLabel>
                              <DetailValue>
                                {s.feedback || '—'}
                              </DetailValue>
                            </DetailItem>
                          </DetailPanel>
                        </DetailCell>
                      </DetailRow>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </TableCard>
      )}
    </MainContent>
    </PageContainer>
  );
};

export default EosSurveyManagementScreen;
